import React, { useMemo, useCallback } from 'react';
import { useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel, getPaginationRowModel, flexRender } from '@tanstack/react-table';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { EventCard } from '@/features/b2b/events/components/EventCard';
import { useScrollToPagination } from '@/hooks/useScrollToPagination';

export const EventsTable = ({
  processedTableData,
  columnFilters,
  pagination,
  onColumnFiltersChange,
  onPaginationChange,
}) => {
  const router = useRouter();

  // Memoize event detail click handler
  const handleEventDetailClick = useCallback((event) => {
    const slug = event.eventSlug;

    if (!slug) {
      console.error('Event slug is undefined:', event);
      return;
    }

    const encodedSlug = encodeURIComponent(slug);
    if (event.eventUrl) {
      window.open(event.eventUrl, '_blank', 'noopener,noreferrer');
    } else {
      // Store event data temporarily and navigate
      if (typeof window !== 'undefined') {
        window.tempEventData = event;
      }
      router.push(`/professionals/events/${encodedSlug}`);
    }
  }, [router]);

  // Define table columns
  const columns = useMemo(
    () =>
      [
        {
          accessorKey: 'combined',
          header: 'Events',
          cell: ({ row, table }) => {
            const event = row.original;
            const isLastRow = row.index === table.getFilteredRowModel().rows.length - 1;

            return (
              <EventCard
                event={event}
                isLastRow={isLastRow}
                onEventDetailClick={handleEventDetailClick}
              />
            );
          },
          filterFn: (row, columnId, value) => {
            const event = row.original;
            const searchText = `${event.eventType || ''} ${event.eventTitle || ''} ${event.eventContent || ''}`.toLowerCase();
            return searchText.includes(value.toLowerCase());
          },
        },
      ],
    [handleEventDetailClick]
  );

  // Initialise React Table
  const table = useReactTable({
    data: processedTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      columnFilters,
      pagination,
    },
    onColumnFiltersChange,
    onPaginationChange,
    manualPagination: false,
    enableColumnFilters: true,
  });

  // Scroll to top when page changes
  useScrollToPagination(pagination.pageIndex, '.events-table');

  return (
    <div key="upcoming-table" className="events-table w-full">
      {table.getFilteredRowModel().rows.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-[#373945] text-base">No matching records found. Please check back later for new events.</p>
        </div>
      ) : (
        <>
          {/* Render table rows */}
          {table.getRowModel().rows.map((row) => (
            <div key={row.id}>
              {flexRender(row.getVisibleCells()[0].column.columnDef.cell, row.getVisibleCells()[0].getContext())}
            </div>
          ))}

          {/* CPD Certificate Section - Mobile Only */}
          <div className="lg:hidden border border-[#CBCDD6] bg-[#F3F4F8] rounded-xl px-6 py-6 mb-[3.25rem]">
            <div className="flex flex-col items-start">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-[#C1E0F5] flex items-center justify-center mr-[0.75rem]">
                    <img src="/img/icons/download-01.svg" alt="KFI icon" className="w-4 h-4"/>
                  </div>
                  <Link
                    href="https://pages.pureretirement.co.uk/CPD-Certificate-Download"
                    className="text-sm font-semibold text-[#000]"
                  >
                    CPD certificate download
                  </Link>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className="ml-6" aria-hidden="true">
                    <path d="M6 12L10 8L6 4" stroke="#5B5D6B" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <p className="text-sm text-[#373945] font-450 pt-2">All our events are structured CPD points and count towards your CPD hours.</p>
            </div>
          </div>

          {/* Pagination controls */}
          {table.getPageCount() > 1 && (
            <div className="flex">
              <div className="flex items-center gap-1">
                {/* Previous button */}
                <button
                  className="inline-flex items-center justify-center min-w-10 h-10 m-1 px-2 rounded-md bg-white text-[#373945] text-sm font-medium cursor-pointer"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  aria-label="Previous page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M12.6667 8H3.33333M3.33333 8L8 12.6667M3.33333 8L8 3.33333"
                      stroke={table.getCanPreviousPage() ? '#006EB7' : '#CBCDD6'}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {/* Page number buttons */}
                {(() => {
                  const currentPage = table.getState().pagination.pageIndex;
                  const totalPages = table.getPageCount();
                  const pageElements = [];

                  // Generate unique page numbers first
                  const pageNumbers = new Set();

                  // Always include the first page
                  if (totalPages > 0) {
                    pageNumbers.add(0);
                  }

                  // Add pages around the current page
                  const start = Math.max(0, currentPage - 2);
                  const end = Math.min(totalPages - 1, currentPage + 2);

                  for (let i = start; i <= end; i++) {
                    pageNumbers.add(i);
                  }

                  // Always include the last page
                  if (totalPages > 1) {
                    pageNumbers.add(totalPages - 1);
                  }

                  // Convert to a sorted array
                  const sortedPages = Array.from(pageNumbers).sort((a, b) => a - b);

                  // Build elements with ellipsis
                  sortedPages.forEach((page, index) => {
                    // Add ellipsis if there's a gap
                    if (index > 0 && page - sortedPages[index - 1] > 1) {
                      pageElements.push(
                        <span key={`ellipsis-${sortedPages[index - 1]}-${page}`} className="px-3 py-2 text-sm text-gray-500">
                          ...
                        </span>
                      );
                    }

                    // Add a page button
                    const isActive = page === currentPage;
                    pageElements.push(
                      <button
                        key={`page-${page}`}
                        className={`inline-flex items-center justify-center min-w-10 h-10 m-1 px-2 border border-gray-200 rounded-md text-sm font-medium cursor-pointer ${
                          isActive
                            ? 'text-white bg-[#006EB7] border-[#006EB7]'
                            : 'bg-white text-[#373945] hover:bg-[#006EB7] hover:text-white'
                        }`}
                        onClick={() => table.setPageIndex(page)}
                      >
                        {page + 1}
                      </button>
                    );
                  });

                  return pageElements;
                })()}

                {/* Next button */}
                <button
                  className="inline-flex items-center justify-center min-w-10 h-10 m-1 px-2 rounded-md bg-white text-[#373945] text-sm font-medium cursor-pointer"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  aria-label="Next page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M3.33333 8H12.6667M12.6667 8L8 3.33333M12.6667 8L8 12.6667"
                      stroke={table.getCanNextPage() ? '#006EB7' : '#CBCDD6'}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
